/**
 * Agent Message Parser - Transforms raw SDK messages into display-friendly timeline
 *
 * Processes the raw JSONB messages from task_message table (Claude Agent SDK format)
 * into structured steps for the AgentTimeline component.
 */

import type { RawAgentMessage } from './tasks-api';

// --- Types ---

export interface AgentTimeline {
  steps: AgentStep[];
}

export interface AgentStep {
  id: string;
  type: 'thinking' | 'sub_agent';
  text: string;
  subAgentName?: string;
  toolCalls: ToolCallEntry[];
}

export interface ToolCallEntry {
  id: string;
  toolName: string;
  displayName: string;
  inputSummary: string;
}

// --- Tool Name Mapping ---

const TOOL_NAME_MAP: Record<string, string> = {
  // SDK built-in tools
  Bash: 'Run Command',
  Glob: 'Find Files',
  Grep: 'Search Content',
  Read: 'Read File',
  Edit: 'Edit File',
  Write: 'Write File',
  Task: 'Launch Sub-Agent',
  Agent: 'Launch Sub-Agent',

  // MCP tools (resolve-ai-mcp server)
  'mcp__resolve-ai-mcp__code_search': 'Search Code',
  'mcp__resolve-ai-mcp__read_file': 'Read File',
  'mcp__resolve-ai-mcp__write_file': 'Write File',
  'mcp__resolve-ai-mcp__list_directory': 'List Directory',
  'mcp__resolve-ai-mcp__jira_read_ticket': 'Read Jira Ticket',
  'mcp__resolve-ai-mcp__jira_add_comment': 'Add Jira Comment',
  'mcp__resolve-ai-mcp__jira_update_status': 'Update Jira Status',
  'mcp__resolve-ai-mcp__create_pull_request': 'Create Pull Request',
  'mcp__resolve-ai-mcp__git_diff': 'Git Diff',
  'mcp__resolve-ai-mcp__git_log': 'Git Log',
  'mcp__resolve-ai-mcp__git_status': 'Git Status',
  'mcp__resolve-ai-mcp__run_tests': 'Run Tests',
  'mcp__resolve-ai-mcp__apply_patch': 'Apply Patch',
};

export function mapToolName(toolName: string): string {
  if (TOOL_NAME_MAP[toolName]) {
    return TOOL_NAME_MAP[toolName];
  }

  // Extract the last segment from MCP tool names (mcp__server__tool_name → tool_name)
  const mcpMatch = toolName.match(/^mcp__[^_]+__(.+)$/);
  if (mcpMatch) {
    return mcpMatch[1];
  }

  return toolName;
}

// --- Parser ---

const MAX_INPUT_SUMMARY_LENGTH = 100;

function summarizeInput(input?: Record<string, unknown>): string {
  if (!input) return '';

  const summary = Object.entries(input)
    .map(([key, value]) => {
      const val = typeof value === 'string' ? value : JSON.stringify(value);
      return `${key}: ${val}`;
    })
    .join(', ');

  if (summary.length > MAX_INPUT_SUMMARY_LENGTH) {
    return summary.slice(0, MAX_INPUT_SUMMARY_LENGTH) + '...';
  }

  return summary;
}

export function parseAgentMessages(messages: RawAgentMessage[] | null): AgentTimeline {
  if (!messages || messages.length === 0) {
    return { steps: [] };
  }

  // Separate master-level assistant messages from child (sub-agent) messages
  const masterMessages: RawAgentMessage[] = [];
  const childByParent = new Map<string, RawAgentMessage[]>();

  for (const msg of messages) {
    if (msg.type !== 'assistant') continue;

    const parentId = msg.parent_tool_use_id;
    if (!parentId) {
      masterMessages.push(msg);
    } else {
      const existing = childByParent.get(parentId) ?? [];
      existing.push(msg);
      childByParent.set(parentId, existing);
    }
  }

  const steps: AgentStep[] = [];
  let stepIndex = 0;

  for (const msg of masterMessages) {
    const content = msg.message?.content;
    if (!Array.isArray(content)) continue;

    for (const block of content) {
      if (block.type === 'text' && block.text?.trim()) {
        steps.push({
          id: `step-${stepIndex++}`,
          type: 'thinking',
          text: block.text,
          toolCalls: [],
        });
      } else if (block.type === 'tool_use' && (block.name === 'Task' || block.name === 'Agent')) {
        const input = block.input as { description?: string; prompt?: string } | undefined;
        const subAgentName = input?.description
          || input?.prompt?.split('\n')[0]
          || 'Sub-Agent';

        // Collect tool calls from child messages
        const children = childByParent.get(block.id!) ?? [];
        const toolCalls: ToolCallEntry[] = [];

        for (const child of children) {
          const childContent = child.message?.content;
          if (!Array.isArray(childContent)) continue;

          for (const childBlock of childContent) {
            if (childBlock.type === 'tool_use' && childBlock.id && childBlock.name) {
              toolCalls.push({
                id: childBlock.id,
                toolName: childBlock.name,
                displayName: mapToolName(childBlock.name),
                inputSummary: summarizeInput(childBlock.input),
              });
            }
          }
        }

        steps.push({
          id: `step-${stepIndex++}`,
          type: 'sub_agent',
          text: '',
          subAgentName,
          toolCalls,
        });
      }
    }
  }

  return { steps };
}
